<template>
  <div
    id="datepicker-inline"
    ref="datepickerRef"
    inline-datepicker
    @changeDate="handleDateChange"
    @changeMonth="handleMonthChange"
  ></div>
</template>

<script setup lang="ts">
import type { DatepickerInterface, DatepickerOptions } from "flowbite";
import type { InstanceOptions } from "flowbite";
import { Datepicker } from "flowbite";
import moment from "moment-timezone";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

const emit = defineEmits(["update:modelValue", "month-change"]);
const datepickerRef = ref<HTMLDivElement | null>(null);
const datepicker = ref<DatepickerInterface | null>(null);
const props = withDefaults(
  defineProps<{
    disabledDates?: Date[];
    minDate?: string | Date | undefined | null;
    modelValue: string | Date | number;
  }>(),
  { minDate: Date.toString(), disabledDates: () => [] }
);

onMounted(() => {
  const options: DatepickerOptions = {
    defaultDatepickerId: null,
    autohide: false,
    format: "mm/dd/yyyy",
    maxDate: null,
    minDate: props.minDate ? moment(props.minDate).format("MM/DD/YYYY") : null,
    orientation: "bottom",
    buttons: false,
    autoSelectToday: 0,
    title: null,
    rangePicker: false
  };
  const instanceOptions: InstanceOptions = {
    id: "datepicker-custom-example",
    override: true
  };

  datepicker.value = new Datepicker(
    datepickerRef.value,
    options,
    instanceOptions
  );
  const datepickerInstance = datepicker.value.getDatepickerInstance();
  datepickerInstance.setOptions({
    datesDisabled: props.disabledDates
  });

  // set the date (or dates if date range picker)
  datepicker.value.setDate(moment(props.modelValue).format("MM/DD/YYYY"));

  // programatically show the datepicker
  datepicker.value.show();
});

onBeforeUnmount(() => {
  datepicker.value?.destroy();
});

const handleDateChange = (e) => {
  if (moment(e.detail.date).utc().isSame(moment(props.modelValue).utc(), "day"))
    return;
  e.detail.date.setUTCHours(12);
  emit("update:modelValue", e.detail.date);
};

const handleMonthChange = (e) => {
  if (
    moment(e.detail.viewDate)
      .utc()
      .isSame(moment(props.modelValue).utc(), "month")
  )
    return;
  const month = moment(e.detail.viewDate).month() + 1;
  emit("month-change", month, e.detail.viewDate);
};

watch(
  () => props.modelValue,
  (newVal, oldVal) => {
    if (!moment(newVal).utc().isSame(moment(oldVal).utc(), "days")) {
      if (newVal) {
        datepicker.value?.setDate(
          moment(newVal).utc().hours(12).format("MM/DD/YYYY")
        );
      } else {
        datepicker.value?.setDate(undefined);
      }
    }
  }
);
</script>

<style lang="css">
.datepicker-cell.disabled {
  @apply bg-gray-100 !text-gray-400 !rounded-none;
}
.datepicker {
  .datepicker-picker,
  .days,
  .datepicker-grid {
    @apply !w-full min-w-[250px];
  }
  .datepicker-picker {
    @apply !shadow-none;
  }
  .datepicker-cell {
    @apply font-semibold;
  }
}
.datepicker-cell.prev,
.datepicker-cell.next {
  @apply !text-gray-400;
}
</style>
